
import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Konva from '../../../../konva';
import { useStrictMode } from '../../../../react-konva';


import {
  CHANGE_POINTER_ICON,
  CLEAR_ANNOTATIONS_SELECTIONS,
  ZOOM_CANVAS,
} from '../../actions';
import {
  ANNOTATE_DRAW_TOOL_IDS,
  DEFAULT_ZOOM_FACTOR,
  POINTER_ICONS,
  TABS_IDS,
  TOOLS_IDS,
} from '../../utils/constants';
import {
  canUsePanMode,
  isCanvasPanMode,
} from '../../utils/canvasInteractionMode';
import { useStore } from '../../hooks';
import useMarqueeSelection from '../../hooks/useMarqueeSelection';
import { endTouchesZooming, zoomOnTouchesMove } from './touchZoomingEvents';
import MarqueeSelectionOverlay from './MarqueeSelectionOverlay';
import { StyledCanvasNode } from './MainCanvas.styled';

const ZOOM_DELTA_TO_SCALE_CONVERT_FACTOR = 0.006;

const CanvasNode = ({ children }) => {
  useStrictMode(true);
  const canvasRef = useRef();
  const {
    dispatch,
    pointerCssIcon,
    tabId,
    toolId,
    canvasWidth,
    canvasHeight,
    canvasScale,
    selectionsIds = [],
    zoom = {},
    editorPreferences = {},
    originalImage,
    shownImageDimensions,
    resize,
    config: { previewPixelRatio, disableZooming },
  } = useStore();
  Konva.pixelRatio = previewPixelRatio;
  const defaultZoomFactor = DEFAULT_ZOOM_FACTOR;
  const isZoomEnabled = !disableZooming && toolId !== TOOLS_IDS.CROP;
  const isPanMode = isCanvasPanMode(editorPreferences);
  const panModeAllowed = canUsePanMode({
    zoom,
    canvasWidth,
    canvasHeight,
    originalImage,
    shownImageDimensions,
    resize,
  });

  const { marqueeRect, isMarqueeActive, suppressCanvasClearRef } =
    useMarqueeSelection(canvasRef);

  const computePanningEnabled = useCallback(() => {
    if (!isZoomEnabled) {
      return false;
    }
    if (isPanMode) {
      return panModeAllowed;
    }
    return (
      tabId !== TABS_IDS.ANNOTATE &&
      tabId !== TABS_IDS.WATERMARK &&
      zoom.factor > defaultZoomFactor
    );
  }, [
    isPanMode,
    isZoomEnabled,
    tabId,
    panModeAllowed,
    zoom.factor,
    defaultZoomFactor,
  ]);

  const [isPanningEnabled, setIsPanningEnabled] = useState(computePanningEnabled);
  const [isStageDragging, setIsStageDragging] = useState(false);
  const [isSpacePanHeld, setIsSpacePanHeld] = useState(false);

  const isAnnotateDrawCursor = useMemo(
    () =>
      !isPanMode &&
      tabId === TABS_IDS.ANNOTATE &&
      toolId &&
      ANNOTATE_DRAW_TOOL_IDS.includes(toolId),
    [isPanMode, tabId, toolId],
  );

  const cursorStyle = useMemo(() => {
    if (isPanMode || isPanningEnabled || isSpacePanHeld) {
      return {
        cursor: isStageDragging ? POINTER_ICONS.DRAGGING : POINTER_ICONS.DRAG,
      };
    }

    return {
      cursor:
        pointerCssIcon === POINTER_ICONS.DEFAULT && isAnnotateDrawCursor
          ? POINTER_ICONS.DRAW
          : pointerCssIcon,
    };
  }, [
    isPanMode,
    isPanningEnabled,
    isSpacePanHeld,
    isStageDragging,
    pointerCssIcon,
    isAnnotateDrawCursor,
  ]);

  const saveZoom = (newZoomProps) => {
    dispatch({
      type: ZOOM_CANVAS,
      payload: newZoomProps,
    });
  };

  const handleCanvasDragStart = (e) => {
    const isStageTarget = e.target.getStage() === e.target;
    if (isStageTarget || isPanMode) {
      setIsStageDragging(true);
    }
  };

  const handlePanModePointerDown = useCallback(
    (e) => {
      if (!isPanMode || !isZoomEnabled || !isPanningEnabled) {
        return;
      }
      if ((e.evt?.touches?.length ?? 0) > 1) {
        return;
      }
      const stage = e.target.getStage();
      if (!stage || e.target === stage) {
        return;
      }
      if (e.evt) {
        stage.setPointersPositions(e.evt);
      }
      stage.startDrag(e);
    },
    [isPanMode, isZoomEnabled, isPanningEnabled],
  );

  const handleCanvasDragEnd = (e) => {
    setIsStageDragging(false);
    if (
      e.currentTarget.draggable() &&
      e.target.nodeType.toLowerCase() === 'stage' &&
      isZoomEnabled &&
      isPanningEnabled
    ) {
      saveZoom({
        factor: zoom.factor,
        x: e.target.x(),
        y: e.target.y(),
        preparedDimensions: true,
      });
    }
  };

  const clearSelections = useCallback(
    (e) => {
      if (suppressCanvasClearRef.current) {
        suppressCanvasClearRef.current = false;
        return;
      }
      if (isPanMode) {
        return;
      }
      e.evt.preventDefault();
      e.currentTarget.container?.().focus();

      if (e.target instanceof Konva.Stage && selectionsIds.length > 0) {
        dispatch({
          type: CLEAR_ANNOTATIONS_SELECTIONS,
        });
      }
    },
    [dispatch, isPanMode, selectionsIds, suppressCanvasClearRef],
  );

  const dragBoundFunc = (pos) => {
    const x = Math.min(0, Math.max(pos.x, canvasWidth * (1 - zoom.factor)));
    const y = Math.min(0, Math.max(pos.y, canvasHeight * (1 - zoom.factor)));

    return {
      x,
      y,
    };
  };

  const handleZoom = (e) => {
    e.evt.preventDefault();
    const newScale =
      (zoom.factor || defaultZoomFactor) +
      e.evt.deltaY * -ZOOM_DELTA_TO_SCALE_CONVERT_FACTOR;

    const pointer = e.currentTarget.getPointerPosition();

    saveZoom({
      ...pointer,
      factor: newScale,
    });
  };

  const preventDraggingIfMultiTouches = (e) => {
    if ((e.evt?.touches?.length ?? 0) > 1) {
      setIsPanningEnabled(false);
    }
  };

  const resetPanningAbility = useCallback(() => {
    if (isSpacePanHeld) {
      return;
    }
    setIsPanningEnabled(computePanningEnabled());
  }, [computePanningEnabled, isSpacePanHeld]);

  const endTouchesZoomingEnablePanning = () => {
    endTouchesZooming(resetPanningAbility);
  };

  const mapKeyboardKeys = (e) => {
    if (isPanMode) {
      return;
    }
    const spacePanAllowed =
      isPanMode
        ? panModeAllowed
        : zoom.factor > defaultZoomFactor;
    if (
      (e.code === 'Space' || e.key === 'Control') &&
      !e.repeat &&
      isZoomEnabled &&
      spacePanAllowed
    ) {
      e.preventDefault();
      setIsSpacePanHeld(true);
      setIsPanningEnabled(true);
      dispatch({
        type: CHANGE_POINTER_ICON,
        payload: {
          pointerCssIcon: POINTER_ICONS.DRAG,
        },
      });
    }
  };

  const revertKeyboardKeysEffect = (e) => {
    if (e.code === 'Space') {
      e.preventDefault();
      setIsSpacePanHeld(false);
      resetPanningAbility();
    }
  };

  const focusCanvasOnEnter = () => {
    if (canvasRef.current) {
      canvasRef.current.container().focus();
    }
  };

  useEffect(() => {
    setIsPanningEnabled(computePanningEnabled());
  }, [computePanningEnabled]);

  useEffect(() => {
    if (isPanMode) {
      dispatch({
        type: CHANGE_POINTER_ICON,
        payload: {
          pointerCssIcon: POINTER_ICONS.DRAG,
        },
      });
      return;
    }

    if (isSpacePanHeld) {
      return;
    }

    dispatch({
      type: CHANGE_POINTER_ICON,
      payload: {
        pointerCssIcon: POINTER_ICONS[isPanningEnabled ? 'DRAG' : 'DEFAULT'],
      },
    });
  }, [dispatch, isPanMode, isPanningEnabled, isSpacePanHeld]);

  useEffect(() => {
    let canvasContainer;
    if (canvasRef.current) {
      canvasContainer = canvasRef.current.container();
      canvasContainer.addEventListener('mouseenter', focusCanvasOnEnter);
      canvasContainer.addEventListener('keydown', mapKeyboardKeys);
      canvasContainer.addEventListener('keyup', revertKeyboardKeysEffect);
    }

    return () => {
      if (canvasContainer) {
        canvasContainer.removeEventListener('mouseenter', focusCanvasOnEnter);
        canvasContainer.removeEventListener('keydown', mapKeyboardKeys);
        canvasContainer.removeEventListener('keyup', revertKeyboardKeysEffect);
      }
    };
  }, [tabId, zoom.factor, defaultZoomFactor, isPanMode, resetPanningAbility]);

  const zoomedResponsiveCanvasScale =
    canvasScale * ((isZoomEnabled && zoom.factor) || defaultZoomFactor);

  return (
    <StyledCanvasNode
      className={`respark_canvas-node${isPanMode ? ' respark_canvas-node--pan-mode' : ''}`}
      tabIndex={-1}
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      scaleX={zoomedResponsiveCanvasScale}
      scaleY={zoomedResponsiveCanvasScale}
      x={(isZoomEnabled && zoom.x) || null}
      y={(isZoomEnabled && zoom.y) || null}
      zoomFactor={(isZoomEnabled && zoom.factor) || defaultZoomFactor}
      onWheel={isZoomEnabled ? handleZoom : undefined}
      onMouseDown={isPanMode ? handlePanModePointerDown : undefined}
      onTouchStart={isPanMode ? handlePanModePointerDown : undefined}
      onTap={clearSelections}
      onClick={clearSelections}
      onTouchMove={
        isZoomEnabled ? (e) => zoomOnTouchesMove(e, saveZoom) : undefined
      }
      onDragStart={(e) => {
        preventDraggingIfMultiTouches(e);
        handleCanvasDragStart(e);
      }}
      onTouchEnd={isZoomEnabled ? endTouchesZoomingEnablePanning : undefined}
      dragBoundFunc={dragBoundFunc}
      draggable={isZoomEnabled && isPanningEnabled && !isMarqueeActive}
      onDragEnd={handleCanvasDragEnd}
      style={cursorStyle}
    >
      {children}
      <MarqueeSelectionOverlay marqueeRect={marqueeRect} />
    </StyledCanvasNode>
  );
};

CanvasNode.propTypes = {
  children: PropTypes.node.isRequired,
};

export default memo(CanvasNode);
